import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit, Input, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { User } from '../../models/user';
import { MonacoPageComponent } from '../../features/lt-shared-ui/lt-monaco/components/monaco-editor/monaco-editor.component';
import { CustomSQLQueryService } from '../../services/sql-queries.service';
import { ToastService } from '../../services/toast.service';
import { CustomSQLQuery } from 'src/app/models/sql-query';
import { LOSTalkerTitleService } from '../../services/lostalker-title-service.service';
import { TypeConstantService } from 'src/app/services/type-constant.service';


@Component({
  selector: 'app-edit-sql-query',
  templateUrl: './edit-sql-query.component.html',
  styleUrls: ['./edit-sql-query.component.scss'],
  providers: [CustomSQLQueryService, LOSTalkerTitleService],
  standalone: false,
})
export class EditSQLQueryComponent implements OnInit, OnDestroy, AfterViewInit {

  constructor(
    private customQueryService: CustomSQLQueryService,
    private typeConstantService: TypeConstantService,
    private toastService: ToastService,
    private route: ActivatedRoute,
    private location: Location,
    private user: User,
    private titleService: LOSTalkerTitleService,
    private router: Router,
  ) { }

  componentDestroyed$ = new Subject<boolean>();
  @ViewChild('monaco') public child: MonacoPageComponent;

  // Embedded (inline) support - mirrors integration-pushback editing flow
  @Input() customSQLQueryId: string = null;
  @Output() itemUpdated = new EventEmitter<CustomSQLQuery>();
  @Output() itemDeleted = new EventEmitter<CustomSQLQuery>();
  @Output() cancel = new EventEmitter<boolean>();

  customSQLQuery: CustomSQLQuery;
  convertingToGeneric = false;
  sub: any;
  name: string;
  genericName: string;
  sql = 'sql';
  monacoData = 'sql';
  monacoOriginalData = '';
  pageLoading = true;
  convertToGeneric = true;
  savingInformation = false;

  ngOnInit(): void {
    this.child?.changeLanguage('sql');
  }

  ngAfterViewInit(): void {
    setTimeout(_ => this.initializeMonacoComponent());
  }

  initializeMonacoComponent(): void {
    // When embedded, the query id is supplied as an input instead of via the route.
    if (this.customSQLQueryId != null) {
      this.loadQuery(this.customSQLQueryId);
      return;
    }

    this.sub = this.route.params
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (params) => {
          this.loadQuery(params['id']);
        },
        error: (_error) => {
          this.toastService.toastCreate('Error Reading Params', 'Danger');
          this.pageLoading = false;
        },
      });
  }

  private loadQuery(id: string): void {
    this.customQueryService.getCustomQueryById(id)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (query) => {
          this.customSQLQuery = query;
          this.name = this.customSQLQuery?.queryName;

          if (this.customSQLQuery != null) {
            this.titleService.setUpTitle('Edit Custom Query - ' + this.customSQLQuery.queryName);
          }

          this.monacoData = query.sqlString;
          this.monacoOriginalData = query.sqlString;
          this.pageLoading = false;
        },
        error: (_error) => {
          this.toastService.toastCreate('Error Getting Queries', 'Danger');
          this.pageLoading = false;
        },
      });
  }

  ngOnDestroy(): void {
    if (this.sub != undefined) {
      this.sub.unsubscribe();
    }
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  goBack(): void {
    this.cancel.emit(true);
  }

  checkAccess(role: string[]): boolean {
    if (this.user.checkAccess(role)) {
      return true;
    } else {
      return false;
    }
  }

  updateDataModel($event) {
    this.monacoData = $event;
  }

  updateCustomQuery(): void {
    this.savingInformation = true;
    this.customSQLQuery.sqlString = this.monacoData;

    this.customQueryService.updateCustomQuery(this.customSQLQuery)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: () => {
          // on success
          this.toastService.toastCreate('Successfully updated ' + this.customSQLQuery.queryName + ' custom SQL Query.', 'Success', {
            autoClose: false,
          });
          this.monacoOriginalData = this.customSQLQuery.sqlString;
          this.savingInformation = false;

          this.itemUpdated.emit(this.customSQLQuery);
        },
        error: (_error) => {
          this.toastService.toastCreate('Error Adding SQL Query', 'Danger');
          this.savingInformation = false;
        },
      });
  }

  deleteCustomQuery(): void {
    this.savingInformation = true;

    const confirmMsg =
      'Are you sure you want to delete the selected Custom SQL Query?';
    if (window.confirm(confirmMsg)) {
      this.customQueryService.deleteCustomQuery(this.customSQLQuery)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe({
          next: () => {
            this.toastService.toastCreate('Successfully deleted ' + this.customSQLQuery.queryName + ' custom SQL Query.', 'Success', {
              autoClose: false,
              keepAfterRouteChange: true,
            });
            this.savingInformation = false;

            this.itemDeleted.emit(this.customSQLQuery);
          },
          error: (_error) => {
            this.toastService.toastCreate('Error Deleting Maintenance', 'Danger');
            this.savingInformation = false;
          },
        });
    }
    this.savingInformation = false;
  }
}
